import { NamespaceCache } from './namespace_cache.js'
import { getSelector } from './selector.js'

/**
 * Create an error for namespace operations
 * @param {string} code - Error code
 * @param {string} message - Error message
 * @param {Object} [extra] - Additional error properties
 * @returns {Error}
 */
function createNamespaceError(code, message, extra = {}) {
  const err = new Error(message)
  err.code = code
  Object.assign(err, extra)
  return err
}

/**
 * Namespace client - simple public key resolver
 */
class NamespaceClient {
  /**
   * @param {import('./types.js').NamespaceClientConfig} config
   */
  constructor(config) {
    this.namespaceUrl = config.namespaceUrl
    this.enableCache = config.enableCache !== false
    this.cache = this.enableCache ? new NamespaceCache(config.cacheTTL || 300000) : null
    this.selector = getSelector(config.selector)
  }

  /**
   * Get public key for a worker name (selects one based on selector strategy)
   * @param {string} name - Worker name
   * @returns {Promise<string>} Public key (hex-encoded)
   * @throws {Error} If worker not found or network error
   */
  async getPublicKey(name) {
    const result = await this._lookup(name)

    if (!result.found) {
      throw createNamespaceError('WORKER_NOT_FOUND', `Worker not found: ${name}`, { name })
    }

    if (!result.workers || result.workers.length === 0) {
      throw createNamespaceError('NO_INSTANCES', `No worker instances available: ${name}`, {
        name
      })
    }

    const worker = this.selector(result.workers, name)
    return worker.public_key
  }

  /**
   * Get worker name for a public key (reverse lookup)
   * @param {string} publicKey - Worker public key (hex-encoded)
   * @returns {Promise<string|null>} Worker name or null if not found
   */
  async getWorkerName(publicKey) {
    // Check reverse cache first
    const cacheKey = `reverse:${publicKey}`
    if (this.enableCache) {
      const cached = this.cache.get(cacheKey)
      if (cached) {
        return cached.name || null
      }
    }

    const result = await this._reverseLookup(publicKey)

    // Handle null/undefined result (e.g., empty results array from server)
    if (!result) {
      return null
    }

    if (this.enableCache) {
      this.cache.set(cacheKey, result)
    }

    return result.found ? result.name : null
  }

  /**
   * Reverse lookup a single public key (internal)
   * @param {string} publicKey - Public key to look up
   * @returns {Promise<{public_key: string, name: string, found: boolean}>}
   * @private
   */
  async _reverseLookup(publicKey) {
    const results = await this._fetchReverseBatch([publicKey])
    return results[0]
  }

  /**
   * Fetch reverse lookup data from server (internal)
   * @param {string[]} publicKeys - Public keys to fetch
   * @returns {Promise<Array<{public_key: string, name: string, found: boolean}>>}
   * @private
   */
  async _fetchReverseBatch(publicKeys) {
    const url = `${this.namespaceUrl}/api/v1/namespace/reverse`

    let response
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ public_keys: publicKeys })
      })
    } catch (err) {
      throw createNamespaceError('NETWORK_ERROR', `Failed to fetch reverse lookup: ${err.message}`, {
        cause: err
      })
    }

    if (!response.ok) {
      throw createNamespaceError(
        'HTTP_ERROR',
        `Namespace service returned ${response.status}: ${response.statusText}`
      )
    }

    const data = await response.json()

    if (data.code !== 0) {
      throw createNamespaceError(
        data.code.toString(),
        data.msg || 'Namespace service returned error'
      )
    }

    return data.data.results
  }

  /**
   * Look up a single worker name (internal)
   * @param {string} name - Worker name to look up
   * @returns {Promise<import('./types.js').LookupResult>}
   * @private
   */
  async _lookup(name) {
    if (this.enableCache) {
      const cached = this.cache.get(name)
      if (cached) {
        return { name: cached.name, workers: cached.workers, found: cached.found }
      }
    }

    const results = await this._fetchBatch([name])
    const result = results[0]

    if (this.enableCache) {
      this.cache.set(name, result)
    }

    return result
  }

  /**
   * Fetch namespace data from server (internal)
   * @param {string[]} names - Names to fetch
   * @returns {Promise<import('./types.js').LookupResult[]>}
   * @private
   */
  async _fetchBatch(names) {
    const url = `${this.namespaceUrl}/api/v1/namespace/list`

    let response
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ names })
      })
    } catch (err) {
      throw createNamespaceError('NETWORK_ERROR', `Failed to fetch namespace: ${err.message}`, {
        cause: err
      })
    }

    if (!response.ok) {
      throw createNamespaceError(
        'HTTP_ERROR',
        `Namespace service returned ${response.status}: ${response.statusText}`
      )
    }

    const data = await response.json()

    if (data.code !== 0) {
      throw createNamespaceError(
        data.code.toString(),
        data.msg || 'Namespace service returned error'
      )
    }

    return data.data.results
  }
}

/**
 * Create a new namespace client
 * @param {import('./types.js').NamespaceClientConfig} config
 * @returns {{getPublicKey: (name: string) => Promise<string>}}
 */
export function newNamespaceClient(config) {
  const client = new NamespaceClient(config)
  return {
    getPublicKey: client.getPublicKey.bind(client),
    getWorkerName: client.getWorkerName.bind(client),
  }
}
